import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ShieldCheck } from 'lucide-react';
import { PLATFORM_ROLES, type PlatformRole, type SuperAdminUser } from '@bliks-io/contracts';
import { useUpdateSuperAdminUser } from '@/pages/admin/api/mutations/useSuperAdminUsers';
import { Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@bliks-io/ui';
import { FormDialog } from '@/components/dialogs/FormDialog';
import { applyApiErrorToForm } from '@/lib/errors';
import type { ApiError } from '@/lib/agent.types';

interface ChangePlatformRoleValues {
  platformRoles: PlatformRole[];
}

interface ChangePlatformRoleDialogProps {
  user: SuperAdminUser | null;
  onOpenChange: (open: boolean) => void;
}

/**
 * Was ein Plattformbenutzer darf, nachträglich (ADR 0042).
 *
 * Eine Wahl wie beim Einladen: zwei Rollen zugleich ergeben heute nichts, was
 * eine allein nicht schon gibt. Den letzten Admin lehnt der Server ab — sonst
 * könnte niemand die Rolle je wieder vergeben.
 */
export function ChangePlatformRoleDialog({ user, onOpenChange }: ChangePlatformRoleDialogProps) {
  const { t } = useTranslation();
  const updateUser = useUpdateSuperAdminUser();

  const onSubmit = async (
    data: ChangePlatformRoleValues,
    form: UseFormReturn<ChangePlatformRoleValues>
  ) => {
    if (!user) return;
    try {
      await updateUser.mutateAsync({ userId: user._id, platformRoles: data.platformRoles });
      onOpenChange(false);
    } catch (error: unknown) {
      applyApiErrorToForm(error as ApiError, form.setError, t, { fields: ['platformRoles'] });
    }
  };

  return (
    <FormDialog<ChangePlatformRoleValues>
      open={user !== null}
      onOpenChange={onOpenChange}
      title={t('adminSettings.superAdmin.changeRoleTitle')}
      description={t('adminSettings.superAdmin.changeRoleDescription', {
        name: user ? `${user.firstName} ${user.lastName}` : ''
      })}
      icon={ShieldCheck}
      defaultValues={{ platformRoles: user?.platformRoles ?? [] }}
      onSubmit={onSubmit}
      submitLabel={t('adminSettings.superAdmin.changeRoleSave')}
      size="xs"
    >
      {({ form }) => {
        const {
          watch,
          setValue,
          formState: { errors }
        } = form;
        const role = watch('platformRoles')?.[0];
        return (
          <div className="grid gap-2">
            <Label htmlFor="changePlatformRole">{t('adminSettings.superAdmin.role')}</Label>
            <Select
              value={role ?? ''}
              onValueChange={(value) =>
                setValue('platformRoles', [value as PlatformRole], { shouldDirty: true })
              }
            >
              <SelectTrigger id="changePlatformRole" aria-invalid={Boolean(errors.platformRoles)}>
                <SelectValue placeholder={t('adminSettings.superAdmin.rolePlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {PLATFORM_ROLES.map((option) => (
                  <SelectItem key={option} value={option}>
                    {t(`adminSettings.superAdmin.roles.${option}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.platformRoles && (
              <p className="text-sm text-destructive">{errors.platformRoles.message}</p>
            )}
            {role && (
              <p className="text-muted-foreground text-xs">
                {t(`adminSettings.superAdmin.roleHint.${role}`)}
              </p>
            )}
          </div>
        );
      }}
    </FormDialog>
  );
}
